import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "ContextProvider", "duration", "onCloseComplete", "onCloseStart", "onOpenComplete", "onOpenStart", "transitionOnMount", "transitionOnUpdate", "Wrapper"];
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import useMounted from 'react-utils/hooks/useMounted';
import styled from 'styled-components';
import { getComponentPropType, isRenderable } from '../utils/propTypes/componentProp';
import { jsx as _jsx } from "react/jsx-runtime";
const activeContextValue = {
  transitioning: true
};
const inactiveContextValue = {
  transitioning: false
};
const getContextValue = transitioning => transitioning ? activeContextValue : inactiveContextValue;
export default function TwoWayTransition(props) {
  const {
      children,
      ContextProvider,
      duration,
      onCloseComplete,
      onCloseStart,
      onOpenComplete,
      onOpenStart,
      transitionOnMount,
      transitionOnUpdate,
      Wrapper
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);

  /** An update that changes `children` between null and non-null triggers open/close */
  const nextOpen = isRenderable(children);
  const [open, setOpen] = useState(transitionOnMount ? !nextOpen : nextOpen);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionActive, setTransitionActive] = useState(false);

  /** A ref to a copy of `children` that's frozen during the close transition */
  const cachedChildrenRef = useRef(children);
  if (nextOpen) cachedChildrenRef.current = children;
  const timeoutHandleRef = useRef(null);
  const rAFHandleRef = useRef(null);
  const mountedRef = useMounted();
  const clearTimeouts = () => {
    if (timeoutHandleRef.current !== null) {
      clearTimeout(timeoutHandleRef.current);
    }
    if (rAFHandleRef.current !== null) {
      cancelAnimationFrame(rAFHandleRef.current);
    }
  };
  const startTransition = () => {
    clearTimeouts();
    if (nextOpen) {
      if (onOpenStart) onOpenStart();
    } else {
      if (onCloseStart) onCloseStart();
    }
    const stopTransition = () => {
      if (nextOpen) {
        if (onOpenComplete) onOpenComplete();
      } else {
        if (onCloseComplete) onCloseComplete();
      }
      if (!mountedRef.current) return;
      if (!nextOpen) cachedChildrenRef.current = null;
      setTransitioning(false);
      setTransitionActive(false);
    };

    // If duration={0}, skip the CSS transition entirely (#6542)
    if (duration === 0) {
      setOpen(nextOpen);
      stopTransition();
      return;
    }
    setTransitioning(true);
    rAFHandleRef.current = requestAnimationFrame(() => {
      rAFHandleRef.current = requestAnimationFrame(() => {
        setOpen(nextOpen);
        setTransitionActive(true);
        timeoutHandleRef.current = setTimeout(stopTransition, duration);
      });
    });
  };

  // Mount/unmount logic
  useEffect(() => {
    if (transitionOnMount && !transitionOnUpdate) {
      startTransition();
    } else if (open && !transitionOnMount) {
      // Some logic requires "open" events to trigger, even when the transition is skipped.
      if (onOpenStart) onOpenStart();
      if (onOpenComplete) onOpenComplete();
    }
    return () => {
      clearTimeouts();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Open/close logic
  useEffect(() => {
    if (transitionOnUpdate && open !== nextOpen) {
      startTransition();
    }
  }, [open, nextOpen, transitionOnUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderedWrapper = /*#__PURE__*/_jsx(Wrapper, Object.assign({}, rest, {
    duration: duration,
    open: open,
    opening: transitionActive && open,
    closing: transitionActive && !open,
    transitioning: transitioning,
    transitionActive: transitionActive,
    children: cachedChildrenRef.current
  }));
  return ContextProvider ? /*#__PURE__*/_jsx(ContextProvider, {
    value: getContextValue(transitioning),
    children: renderedWrapper
  }) : renderedWrapper;
}
if (process.env.NODE_ENV !== 'production') {
  TwoWayTransition.propTypes = {
    children: PropTypes.node,
    ContextProvider: PropTypes.elementType,
    duration: PropTypes.number.isRequired,
    onCloseComplete: PropTypes.func,
    onCloseStart: PropTypes.func,
    onOpenComplete: PropTypes.func,
    onOpenStart: PropTypes.func,
    transitionOnMount: PropTypes.bool.isRequired,
    transitionOnUpdate: PropTypes.bool.isRequired,
    Wrapper: getComponentPropType({
      propTypes: {
        duration: PropTypes.number,
        open: PropTypes.bool.isRequired,
        transitioning: PropTypes.bool.isRequired,
        transitionActive: PropTypes.bool.isRequired,
        closing: PropTypes.bool.isRequired,
        opening: PropTypes.bool.isRequired
      }
    }).isRequired
  };
}
TwoWayTransition.defaultProps = {
  transitionOnMount: false,
  transitionOnUpdate: true
};
TwoWayTransition.displayName = 'TwoWayTransition';
const PlainWrapper = styled.div.attrs({
  'data-transition-wrapper': true
}).withConfig({
  displayName: "TwoWayTransition__PlainWrapper",
  componentId: "sc-1e29li8-0"
})([""]);
PlainWrapper.displayName = 'TwoWayTransition.PlainWrapper';
TwoWayTransition.PlainWrapper = PlainWrapper;